import React, { useState, useEffect } from 'react';
import { Flame, Calendar, TrendingUp, CheckCircle, Circle } from 'lucide-react';
import { SmallText, BodyText } from './brand/BrandTypography';

/**
 * ProgressCard - Shows streak and weekly progress on the dashboard
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const ProgressCard = ({ className = '' }) => {
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProgress = async () => {
            try {
                const token = localStorage.getItem('co_regulate_token');
                if (!token) {
                    setLoading(false);
                    return;
                }

                const res = await fetch(`${API_URL}/api/stats/progress`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (res.ok) {
                    const data = await res.json();
                    setProgress(data);
                }
            } catch (error) {
                console.error('Failed to fetch progress:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgress();
    }, []);

    if (loading) {
        return (
            <div className={`glass-card rounded-2xl p-5 ${className}`}>
                <div className="animate-pulse flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#FFBB98]/50 rounded-full" />
                    <div className="flex-1 space-y-2">
                        <div className="h-4 bg-[#FFBB98]/50 rounded w-1/3" />
                        <div className="h-3 bg-[#FFBB98]/50 rounded w-2/3" />
                    </div>
                </div>
            </div>
        );
    }

    if (!progress) return null;

    const { streak, week_sessions, daily_sessions, encouragement } = progress;

    // Don't show if user has no activity
    if (streak === 0 && week_sessions === 0) {
        return null;
    }

    return (
        <div className={`glass-card rounded-2xl p-5 ${className}`}>
            {/* Main Stats Row */}
            <div className="flex items-center gap-4 mb-4">
                {/* Streak Circle */}
                <div className="relative">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                        streak > 0 
                            ? 'bg-gradient-to-br from-[#F5A962] to-[#E8935A]' 
                            : 'bg-[#FFBB98]'
                    }`}>
                        <Flame className={`w-6 h-6 ${streak > 0 ? 'text-white' : 'text-[#7D8E95]'}`} />
                    </div>
                    {streak > 0 && (
                        <div className="absolute -bottom-1 -right-1 bg-white rounded-full px-1.5 py-0.5 shadow-sm border border-[#FFBB98]">
                            <span className="text-xs font-bold text-[#E8935A]">{streak}</span>
                        </div>
                    )}
                </div>

                {/* Stats Text */}
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        {streak > 0 ? (
                            <span className="font-semibold text-[#1D6B6B]">
                                {streak} day streak!
                            </span>
                        ) : (
                            <span className="font-medium text-[#7D8E95]">
                                {week_sessions} session{week_sessions !== 1 ? 's' : ''} this week
                            </span>
                        )}
                    </div>
                    <SmallText className="text-[#7D8E95]">
                        {encouragement}
                    </SmallText>
                </div>

                {/* Week sessions badge */}
                {streak > 0 && week_sessions > 0 && (
                    <div className="flex flex-col items-center">
                        <span className="text-lg font-semibold text-[#1D6B6B]">{week_sessions}</span>
                        <SmallText className="text-[#7D8E95]">this week</SmallText>
                    </div>
                )}
            </div>

            {/* Weekly Progress Dots */}
            {daily_sessions && daily_sessions.length > 0 && (
                <div className="flex items-center justify-between gap-1 pt-3 border-t border-[#FFBB98]/50">
                    {daily_sessions.map((day, idx) => (
                        <div key={idx} className="flex flex-col items-center gap-1.5">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                                day.has_session 
                                    ? 'bg-[#1D6B6B] shadow-sm' 
                                    : 'bg-[#FFBB98]/60'
                            }`}>
                                {day.has_session ? (
                                    <CheckCircle className="w-4 h-4 text-white" />
                                ) : (
                                    <Circle className="w-3 h-3 text-[#C5C5C2]" />
                                )}
                            </div>
                            <span className={`text-[10px] font-medium ${
                                day.has_session ? 'text-[#1D6B6B]' : 'text-[#7D8E95]'
                            }`}>
                                {day.day}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProgressCard;
